import React from 'react';
import { Row, Col, Badge } from 'react-bootstrap';

/**
 * SettlementReceiptPreview - A preview component for settlement receipt customization
 * Used in BusinessSettingsPage to show how the settlement receipt will look
 */
const SettlementReceiptPreview = ({ settlement, options = {} }) => {
    // Helper to determine text color (black or white) based on background brightness
    const getContrastingColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        if (hex.length !== 6) return '#000000';

        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastingColor(settlement?.invoice_color || '#ffffff');
    const mutedColor = textColor === '#FFFFFF' ? '#e0e0e0' : '#666666';

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(amount);
    };

    // Default values for preview
    const supplierName = settlement.supplier_name || 'Proveedor ABC S.A.S';
    const supplierTaxId = settlement.supplier_tax_id || '900.987.654-3';
    const grossAmount = settlement.gross_amount || 500000;
    const totalDeductions = settlement.total_deductions || 50000;
    const netAmount = grossAmount - totalDeductions;

    return (
        <div
            className="receipt-container"
            style={{
                backgroundColor: settlement.invoice_color || '#fff',
                color: textColor,
                maxWidth: '380px',
                margin: '0 auto',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                fontSize: '0.8rem'
            }}
        >
            {/* Header with logo and business info */}
            <div className="text-center mb-3">
                {settlement.logo_url && (
                    <img
                        src={settlement.logo_url}
                        alt="Logo"
                        style={{ maxWidth: '100px', maxHeight: '80px', objectFit: 'contain', marginBottom: '8px' }}
                    />
                )}
                <h6 className="fw-bold mb-0">{settlement.business_name || 'Nombre del Negocio'}</h6>
                {settlement.business_address && <div className="small" style={{ color: textColor }}>{settlement.business_address}</div>}
                {settlement.business_phone && <div className="small" style={{ color: textColor }}>Tel: {settlement.business_phone}</div>}
                {settlement.fiscal_id && (
                    <div style={{ fontSize: '0.75rem', color: mutedColor }}>
                        {settlement.fiscal_id_type ? `${settlement.fiscal_id_type}: ` : ''}{settlement.fiscal_id}
                    </div>
                )}
            </div>

            {/* Title */}
            <div className="border-top border-bottom py-2 mb-3" style={{ borderColor: mutedColor + '!important' }}>
                <h6 className="text-center fw-bold mb-0" style={{ fontSize: '0.85rem' }}>LIQUIDACIÓN DE PROVEEDOR</h6>
                <div className="text-center small" style={{ color: mutedColor }}>#{settlement.settlement_id || '001'}</div>
            </div>

            {/* Supplier Info */}
            {options.show_supplier_info !== false && (
                <div className="mb-3" style={{ fontSize: '0.75rem' }}>
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>Proveedor:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierName}</Col>
                    </Row>
                    {supplierTaxId && (
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>NIT/CC:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierTaxId}</Col>
                        </Row>
                    )}
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>Período:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>
                            {settlement.period_start ? new Date(settlement.period_start).toLocaleDateString('es-CO') : '01/01/2026'} - {settlement.period_end ? new Date(settlement.period_end).toLocaleDateString('es-CO') : '15/01/2026'}
                        </Col>
                    </Row>
                </div>
            )}

            {/* Amount Summary - Deductions */}
            {options.show_deductions !== false && (
                <div className="mb-3">
                    <h6 className="fw-bold border-bottom pb-1 mb-2" style={{ color: textColor, borderColor: mutedColor, fontSize: '0.8rem' }}>
                        Resumen
                    </h6>
                    <Row className="mb-1" style={{ fontSize: '0.75rem' }}>
                        <Col xs={8} style={{ color: textColor }}>Monto Bruto:</Col>
                        <Col xs={4} className="text-end" style={{ color: textColor }}>{formatCurrency(grossAmount)}</Col>
                    </Row>
                    <div className="small" style={{ color: mutedColor }}>Deducciones:</div>
                    <Row className="mb-1 small">
                        <Col xs={3} style={{ color: mutedColor }}>05/01</Col>
                        <Col xs={5} style={{ color: mutedColor }}>Transporte</Col>
                        <Col xs={4} className="text-end text-danger">-{formatCurrency(30000)}</Col>
                    </Row>
                    <Row className="mb-1 small">
                        <Col xs={3} style={{ color: mutedColor }}>10/01</Col>
                        <Col xs={5} style={{ color: mutedColor }}>Materiales</Col>
                        <Col xs={4} className="text-end text-danger">-{formatCurrency(20000)}</Col>
                    </Row>
                    <Row className="mb-1 mt-2" style={{ fontSize: '0.75rem' }}>
                        <Col xs={8} style={{ color: textColor }}>Total Deducciones:</Col>
                        <Col xs={4} className="text-end text-danger fw-bold">-{formatCurrency(totalDeductions)}</Col>
                    </Row>
                    <div className="border-top pt-2 mt-2" style={{ borderColor: mutedColor }}>
                        <Row>
                            <Col xs={7} className="fw-bold" style={{ color: textColor, fontSize: '0.9rem' }}>NETO A PAGAR:</Col>
                            <Col xs={5} className="text-end fw-bold" style={{ color: textColor, fontSize: '1rem' }}>{formatCurrency(netAmount)}</Col>
                        </Row>
                    </div>
                </div>
            )}

            {/* Payment Info */}
            {options.show_payment_info !== false && (
                <div className="border-top pt-2 mb-3" style={{ borderColor: mutedColor, fontSize: '0.75rem' }}>
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>Estado:</Col>
                        <Col xs={8} className="text-end">
                            <Badge bg="warning" text="dark">PENDIENTE</Badge>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Notes */}
            {options.show_notes !== false && settlement.notes && (
                <div className="mb-3 p-2 rounded" style={{ backgroundColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.1)' : '#f9f9f9', fontSize: '0.7rem' }}>
                    <small className="fw-bold">Notas:</small>
                    <div style={{ color: mutedColor }}>{settlement.notes || 'Pago acordado para el día 20'}</div>
                </div>
            )}

            {/* Footer */}
            <div className="text-center mt-3 pt-2 border-top" style={{ borderColor: mutedColor }}>
                {/* QR Code */}
                {options.show_qr_code !== false && settlement.qr_code_url && (
                    <div className="mb-2">
                        <img
                            src={settlement.qr_code_url}
                            alt="QR Code"
                            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                        />
                    </div>
                )}

                <div className="small fst-italic" style={{ color: mutedColor }}>
                    {settlement.invoice_footer || 'Gracias por su colaboración.'}
                </div>
                <div className="mt-1">
                    <small style={{ fontSize: '0.6rem', color: mutedColor }}>Powered by Evazard</small>
                </div>
            </div>
        </div>
    );
};

export default SettlementReceiptPreview;
